// Import styles for the component
import "./style.css";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Phone, X } from "lucide-react";
import { useSelector } from "react-redux";
import { cn } from "./utils";

import { selectCustomerInfo } from "src/stores/calllogs";

interface CallModalEnhancedProps {
  state: "incoming" | "outbound" | "active" | "idle";
  callerName?: string;
  callerNumber?: string;
  onTransitionToOutbound?: () => void;
  callerInfo?: any;
  onClose?: () => void; // Added onClose prop
}

export function CallModalEnhanced({
  state,
  callerName,
  callerNumber,
  callerInfo,
  onTransitionToOutbound,
  onClose, // Destructure onClose
}: CallModalEnhancedProps) {
  /* ---------------- STATE ---------------- */
  const [position, setPosition] = useState({ x: 50, y: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [callTime, setCallTime] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  console.log(state, "statestatestatestatestatestate");

  /* ---------------- 🔑 ALWAYS CALL HOOKS FIRST ---------------- */
  const customerInfo = useSelector(selectCustomerInfo);
  const customer = customerInfo?.data?.result?.customer;
  console.log(callerInfo, "callerInfocallerInfocallerInfo");
  console.log(customerInfo, "customerInfocustomerInfocustomerInfo");

  const renderCommaPills = (value) => {
    if (!value || typeof value !== "string") {
      return <></>;
    }

    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => (
        <span
          key={index}
          style={{
            background: "linear-gradient(135deg, #233580 0%, #902034 100%)",
          }}
          className="inline-flex items-center justify-center
                   min-w-[35px] h-[35px]
                   rounded-lg
                   text-white
                   text-[16px] font-bold
                   border border-white/30
                   shadow-sm"
        >
          {item}
        </span>
      ));
  };

  /* ---------------- OPEN / CLOSE ---------------- */
  useEffect(() => {
    if (
      state === "incoming" ||
      state === "outbound" ||
      state === "active" ||
      state === "idle"
    ) {
      setShouldRender(true);
      requestAnimationFrame(() => setIsVisible(true));
    }
  }, [state]);

  /* ---------------- TIMER ---------------- */
  useEffect(() => {
    if (state !== "active") {
      setCallTime(0);
      return;
    }

    const interval = setInterval(() => {
      setCallTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [state]);

  /* ---------------- DRAG ---------------- */
  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    // Prevent dragging if clicking on close button
    if ((e.target as HTMLElement).closest(".close-button")) {
      return;
    }

    setIsDragging(true);

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    setDragOffset({
      x: clientX - position.x,
      y: clientY - position.y,
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

      const maxX = window.innerWidth - (modalRef.current?.offsetWidth || 320);
      const maxY = window.innerHeight - (modalRef.current?.offsetHeight || 420);

      setPosition({
        x: Math.max(0, Math.min(clientX - dragOffset.x, maxX)),
        y: Math.max(0, Math.min(clientY - dragOffset.y, maxY)),
      });
    };

    const stop = () => setIsDragging(false);

    document.addEventListener("mousemove", handleMove as EventListener);
    document.addEventListener("touchmove", handleMove as EventListener);
    document.addEventListener("mouseup", stop);
    document.addEventListener("touchend", stop);

    return () => {
      document.removeEventListener("mousemove", handleMove as EventListener);
      document.removeEventListener("touchmove", handleMove as EventListener);
      document.removeEventListener("mouseup", stop);
      document.removeEventListener("touchend", stop);
    };
  }, [isDragging, dragOffset]);

  /* ---------------- HELPERS ---------------- */
  const formatTime = (sec: number) =>
    `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

  const handleClick = () => {
    if (state === "outbound") {
      onTransitionToOutbound?.();
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering handleClick
    onClose?.();
  };

  /* ---------------- 🚨 SAFE EARLY RETURN ---------------- */
  if (!shouldRender) return null;

  /* ---------------- RENDER ---------------- */
  return (
    <div
      ref={modalRef}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      onClick={handleClick} // Only allows click for "active" or "outbound"
      role={state === "active" ? "button" : undefined}
      tabIndex={state === "active" ? 0 : undefined}
      style={{
        transform: `translate(${position.x}px, ${position.y}px) scale(${isVisible ? 1 : 0.85}) translateY(${isVisible ? "0px" : "40px"})`,
        opacity: isVisible ? 1 : 0,
        transition: isDragging
          ? "none"
          : "transform 0.45s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease",
        background:
          "linear-gradient(135deg, rgb(161 52 67) 0%, rgb(35 53 128 / 85%) 100%)",
        zIndex: 9999,
      }}
      className={cn(
        "fixed w-72 rounded-3xl shadow-2xl border border-slate-700",
        "select-none touch-none",
        state === "active" || state === "outbound"
          ? "cursor-pointer"
          : "cursor-default", // Set cursor to default if incoming
      )}
    >
      {/* Close Button - Only show for active state */}

      {/* Ring waves */}
      {(state === "incoming" || state === "outbound") && (
        <>
          <span className="call-wave" />
          <span className="call-wave delay" />
        </>
      )}

      <div className="relative p-6 flex flex-col items-center gap-3">
        <div
          className={cn(
            "w-20 h-20 rounded-full flex items-center justify-center shadow-xl",
            state === "incoming"
              ? "bg-gradient-to-br from-emerald-400 to-emerald-600 animate-scale-pulse"
              : state === "idle"
                ? "bg-gradient-to-br from-red-500 to-red-700"
                : "bg-gradient-to-br from-blue-400 to-blue-600",
          )}
        >
          <Phone
            className={cn(
              "w-10 h-10",
              state === "incoming" && "animate-phone-ring",
              state === "idle" ? "text-white" : "text-white",
            )}
          />
        </div>

        {customer ? (
          <div className="text-center space-y-1">
            <p className="text-lg font-semibold text-white">
              {customer.country_code} {customer.phone}
            </p>
            <p className="text-sm text-slate-300">{customer.fullname}</p>
          </div>
        ) : (
          <div className="text-center space-y-1">
            <p className="text-lg font-semibold text-white">
              {callerNumber || callerInfo?.caller || "Unknown"}
            </p>
            <p className="text-sm text-slate-400">
              {callerName || "New Customer"}
            </p>
          </div>
        )}

        {callerInfo && (
          <div className="text-center space-y-2 w-full">
            {callerInfo?.callActionInput && (
              <div className="flex flex-wrap gap-1.5 justify-center">
                {renderCommaPills(callerInfo.callActionInput)}
              </div>
            )}
            {callerInfo?.callActionQueue && (
              <p className="text-sm text-slate-300">
                {callerInfo.callActionQueue}
              </p>
            )}
            {callerInfo?.ivrMenuName && (
              <p className="text-sm text-slate-300">{callerInfo.ivrMenuName}</p>
            )}
          </div>
        )}

        <p
          className={cn(
            "text-sm font-semibold",
            state === "incoming"
              ? "text-emerald-300"
              : state === "idle"
                ? "text-red-400"
                : state === "outbound"
                  ? "text-blue-300"
                  : "text-emerald-300",
          )}
        >
          {state === "incoming"
            ? "Incoming Call"
            : state === "outbound"
              ? "Outgoing Call"
              : state === "idle"
                ? "Disconnected"
                : "Active Call"}
        </p>
        {/* 
        {state === "active" && (
          <p className="text-sm font-mono text-slate-300">
            {formatTime(callTime)}
          </p>
        )} */}
      </div>
    </div>
  );
}
